/**
 * Place only simple/testable (check ./cypress/e2e/functions-1.cy.ts) functions here.
 * For Angular/Rxjs dependend functions use ./functions-angular.ts!
 */
import * as dayjs from 'dayjs';
import { CurrencyCodesEnum, FormInstrument, InstrumentChartCardInput, LanguagesEnum } from './charts/charts';
import { Decimal } from 'decimal.js';
import { ApiModelPortfolioChartOut, InstrumentDetailFullOut, MoneyPoint } from '../services/rest-api/rest-api';
import * as Sentry from '@sentry/browser';
import { desktopWidthBreakpoint, SortDirections } from './constants';

export function transformPriceForChartAxis(
    value: string | number,
    currencyCode: keyof typeof CurrencyCodesEnum = 'CZK',
    language: keyof typeof LanguagesEnum = 'cs-CZ',
    isPercent = false,
    formatNotation: 'standard' | 'compact' = 'compact',
    minOneDecimalPercent?: boolean,
  ): string | number {
  if(+value === 0) {
    return value;
  }

  if(isPercent) {
    return transformPercent(parseLocaleNumber(value + '', language), false, minOneDecimalPercent ? 1 : null);
  }

  // display decimal point for 0,x values
  if (+value > 0 && +value < 1) {
    value = parseFloat((value + '').replaceAll(' ', '').replaceAll(',', ''));
  } else {
    value = parseInt((value + '').replaceAll(' ', '').replaceAll(',', ''));
  }

  const formatOptions: Intl.NumberFormatOptions = {
    style: 'currency',
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
    currency: currencyCode,
    notation: formatNotation,
  };

  try {
    return new Intl.NumberFormat(language, formatOptions).format(value);
  } catch (error) {
    // used to debugg "Value negative out of range for Intl.NumberFormat options property signDisplay" error
    Sentry.addBreadcrumb({ data: { language } });
    Sentry.addBreadcrumb({ data: { formatOptions } });
    Sentry.addBreadcrumb({ data: { value } });
    Sentry.addBreadcrumb({ data: { error } });
    Sentry.captureException('Debugg message: Failed to parse (3) following number:' + value);
    return value + '';
  }
}

export function transformPriceForChartTooltip(
  value: string | number,
  currencyCode: keyof typeof CurrencyCodesEnum = 'CZK',
  isPercent = false,
  language: keyof typeof LanguagesEnum = 'cs-CZ',
  minOneDecimalPercent?: boolean,
): string {
  const parsedValue = value + '';

  if(isPercent) {
    return transformPercent(parseLocaleNumber(value + '', language), true, minOneDecimalPercent ? 1 : null);
  }

  if(+value === 0) {
    return value + '';
  }

  const formatOptions: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  };

  try {
    return new Intl.NumberFormat(language, formatOptions).format(parseLocaleNumber(parsedValue + '', language));
  } catch (error) {
    // used to debugg "Value negative out of range for Intl.NumberFormat options property signDisplay" error
    Sentry.addBreadcrumb({ data: { language: language } });
    Sentry.addBreadcrumb({ data: { formatOptions } });
    Sentry.addBreadcrumb({ data: { parsedValue } });
    Sentry.addBreadcrumb({ data: { error } });
    Sentry.captureException('Debugg message: Failed to parse (4) following number:' + parsedValue);
    return value + '';
  }
}

export function parseLocaleNumber(stringNumber: string, locale: string): number {
  try {
    const { thousandSeparator, decimalSeparator } = getBrowserNumberSeparators();

    return parseFloat(stringNumber
        .replace(new RegExp('\\' + thousandSeparator, 'g'), '')
        .replace(new RegExp('\\' + decimalSeparator), '.')
    );
  } catch (error) {
    // used to debugg "Value negative out of range for Intl.NumberFormat options property signDisplay" error
    Sentry.addBreadcrumb({ data: { locale } });
    Sentry.addBreadcrumb({ data: { stringNumber } });
    Sentry.addBreadcrumb({ data: { error } });
    Sentry.captureException('Debugg message: Failed to parse (5) following number:' + stringNumber);
    return parseFloat(stringNumber);
  }
}

/**
 * generate "random" number with both separators by browser to determine what separators should be used
 */
function getBrowserNumberSeparators(): { thousandSeparator: string, decimalSeparator: string } {
  const numberWithGroup = new Intl.NumberFormat().format(11111); // e.g., '11,111'
  const numberWithDecimal = new Intl.NumberFormat().format(1.1); // e.g., '1.1'
  const thousandSeparator = numberWithGroup.match(/\D+/g)?.[0] || '';
  const decimalSeparator = numberWithDecimal.match(/\D+/g)?.[0] || '.';
  return { thousandSeparator, decimalSeparator };
}

export type AvailableCurrencySymbols = '€' | 'Kč';

export function getCurrencyFromCode(currencyCode: keyof typeof CurrencyCodesEnum): AvailableCurrencySymbols {
  return currencyCode === 'EUR' ? '€' : 'Kč';
}

function transformPercent(value: number, handleLessThan = true, minDecimal?: number | null): string {
  if(value === 0) {
    return '0 %';
  } else if(value < 0.001 && value > -0.001 && handleLessThan) {
    return value < 0 ? '> -0.001 %' : '< 0.001 %';
  } else if(value < 0.01 && value > -0.01) {
    return value.toFixed(minDecimal ?? 3) + ' %';
  } else if(value < 0.1 && value > -0.1) {
    return value.toFixed(minDecimal ?? 2) + ' %';
  } else if (value < 1 && value > -1) {
    return value.toFixed(minDecimal ?? 1) + ' %';
  } else {
    return value.toFixed(minDecimal ?? 0) + ' %';
  }
}

export function isRelativeUrl(url: string): boolean {
  const r = new RegExp('^(?:[a-z+]+:)?//', 'i');
  return !r.test(url);
}

export function getInstrumentsWeightSum(instruments: FormInstrument[]): number {
  return Decimal.sum(...instruments.map(instrument => instrument.value)).div(100).toNumber();
}

export function handleDevelopmentTimeTicks(
    index: number,
    labels: string[] | number[],
    userLocalization: 'CZK' | 'EUR',
    callbackLabel: string | number,
  ): number | string {
  try {
    dayjs.locale(userLocalization === 'EUR' ? 'en' : 'cs');
    const dateValue = dayjs(labels[index]);

    const uniqueYears = new Set(labels.map(xLabel => dayjs(xLabel).year()));

    const yearDifference = Math.max(...uniqueYears) - Math.min(...uniqueYears)

    if(yearDifference > 3) {
      return dateValue.format('YYYY');
    } else {
      return dateValue.format('MM/YY');
    }
  } catch (error) {
    console.error('Wrong chart label!', error);
    return callbackLabel;
  }
}

export function handleDevelopmentTimeTicksForPdf(
    index: number,
    labels: string[] | number[],
    userLocalization: 'CZK' | 'EUR',
    callbackLabel: string | number,
  ): number | string {
  try {
    dayjs.locale(userLocalization === 'EUR' ? 'en' : 'cs');
    const dateValue = dayjs(labels[index]);

    const uniqueYears = new Set(labels.map(xLabel => dayjs(xLabel).year()));

    const minYear = Math.min(...uniqueYears);
    const periodBetween = +dateValue.get('year') - minYear;
    return periodBetween === 0 ? '' : getCzechYears(periodBetween);

  } catch (error) {
    console.error('Wrong chart label!', error);
    return callbackLabel;
  }
}

// TODO: how to handle this? can we have these checks labels here or somehow handle with trans service?
function getCzechYears(num: number) {
  if (num === 1) return '1 rok';
  else if (num > 1 && num < 5) return num + ' roky';
  else return num + ' let';
}

export function getMiddleDate(start: number, end: number): number {
  const startDate = dayjs(start);
  const endDate = dayjs(end);
  const diffInMilliseconds = endDate.diff(startDate);
  const midpointDate = startDate.add(diffInMilliseconds / 2);

  return midpointDate.valueOf();
}

export function adjustDevelopmentTimeLabels(dates: number[]): number[] {
  const maxLabels = window.innerWidth > desktopWidthBreakpoint ? 14 : 8;
  const results: number[] = [];

  if(dates.length < 5) {
    for (const index of dates.keys()) {
      results.push(dates[index]);

      if (index < dates.length && dates[index + 1]) {
        results.push(getMiddleDate(dates[index], dates[index + 1]));
      }
    }
  } else if (dates.length > maxLabels) {
    results.push(dates[0]);

    for (let index = 2; index < dates.length - 2; index++) {
      results.push(dates[index]);
      index++;
    }

    results.push(dates[dates.length - 1]);

  } else {
    results.push(...dates);
  }

  return results.sort();
}

export function lighterColor(hexColor: string, magnitude: number): string {
  hexColor = hexColor.replace('#', '');
  if (hexColor.length === 6) {
      const decimalColor = parseInt(hexColor, 16);
      let r = (decimalColor >> 16) + magnitude;
      /* eslint-disable @typescript-eslint/no-unused-expressions */
      r > 255 && (r = 255);
      r < 0 && (r = 0);
      let g = (decimalColor & 0x0000ff) + magnitude;
      g > 255 && (g = 255);
      g < 0 && (g = 0);
      let b = ((decimalColor >> 8) & 0x00ff) + magnitude;
      b > 255 && (b = 255);
      b < 0 && (b = 0);
      /* eslint-enable @typescript-eslint/no-unused-expressions */
      return `#${(g | (b << 8) | (r << 16)).toString(16)}`;
  } else {
      return hexColor;
  }
};

export function prevTickTooClose(tick: string | number, prevTick?: string | number, type?: 'year' | 'month'): boolean {
  if(!prevTick || parseInt(tick + '') < parseInt(prevTick + '')) {
    return false;
  }

  const tickDate = dayjs(tick);
  const prevTickDate = dayjs(prevTick);

  if(type) {
    if(type === 'year' && tickDate.diff(prevTickDate, 'month') < 11) {
      return true;
    }

    if(type === 'month' && tickDate.diff(prevTickDate, 'month') < 1) {
      return true;
    }
  }

  if(tickDate.diff(prevTickDate, 'day') < 7) {
    return true;
  }

  //fixme: hotfix for chart overflowing labels,
  if(!type && tickDate.diff(prevTickDate, 'day') < 10) {
    return true;
  }

  return false;
}

export function getDepositOrWithdrawValue(transactionX: number, transactionsInTime: MoneyPoint[]): number {
  const correspondingDeposit = transactionsInTime.find(deposit => deposit.x === transactionX);
  if(correspondingDeposit) {
    if(correspondingDeposit.sumOfDeposits) {
      return correspondingDeposit.sumOfDeposits;
    } else if (correspondingDeposit.sumOfWithdrawals) {
      return -correspondingDeposit.sumOfWithdrawals;
    }
  }

  return NaN;
}

export function skipCauseLongFirstDepositGap(index: number, labels: string[] | number[]): boolean {
  if(index === 1) {
    const firstLabelGap = dayjs(labels[1]).diff(dayjs(labels[0]), 'day');
    const currentToLastLabelGap = dayjs(labels[labels.length - 1]).diff(dayjs(labels[index]), 'day');
    const averageDiffInDays = getAverageDiffInDays(labels)
    if (
      firstLabelGap > 7 &&
      labels.length > 3 &&
      firstLabelGap > (3 * averageDiffInDays) && // 3x average should handle skipping
      currentToLastLabelGap < (averageDiffInDays * 10) // display label if gap to last label is big enough
    ) {
        return true;
    }
  } else if (index > 1 && index !== labels.length - 1) {
    const firstLabelGap = dayjs(labels[1]).diff(dayjs(labels[0]), 'day');
    // const currentToLastLabelGap = dayjs(labels[labels.length - 1]).diff(dayjs(labels[index]), 'day');

    if(
      firstLabelGap > 7 &&
      labels.length > 3
    ) {
      return true;
    }
  }

  return false;
}

function getAverageDiffInDays(labels: string[] | number[]): number {
  let averageDiff = 0;

  labels.map(
    (label, index) => {
      if(index > 1) {
        averageDiff += dayjs(labels[index]).diff(dayjs(labels[index-1]), 'day')
      }
    }
  );

  return averageDiff/(labels.length - 2);
}

export function riskNameFromNumber(riskScore: number | string): string {
  riskScore = typeof riskScore === 'string' ? parseInt(riskScore, 10) : riskScore;

  switch (riskScore) {
    case 1:
      return 'defensive';
    case 2:
      return 'cautious';
    case 3:
      return 'conservative';
    case 4:
      return 'balanced';
    case 5:
      return 'dynamic';
    case 6:
      return 'stock';
    default:
      throw new Error('Unknown risk score!');
  }
}

export function mapIsinsToWeights(input: InstrumentChartCardInput): Record<string, number> {
  const result: Record<string, number> = {};

  input.chartData.forEach(item => result[item.instrument.isin] = item.weight);

  return result;
}

export const sortInstruments = (direction: SortDirections | null) => (a: InstrumentDetailFullOut, b: InstrumentDetailFullOut): number => {
  if (a.okSmartBond !== b.okSmartBond) {
    return a.okSmartBond ? -1 : 1;
  } else if (direction === SortDirections.HIGH_RISK) {
    return b.riskScore - a.riskScore;
  } else if (direction === SortDirections.LOW_RISK) {
    return a.riskScore - b.riskScore;
  } else {
    // default by id
    return a.instrumentId - b.instrumentId;
  }
}

/**
 * Dont use/deprecated, used as hotfix/sure check of RxJs error object after throwError changed in v7
 * https://rxjs.dev/deprecations/breaking-changes#throwerror
 * @output boolean - true if @param error is HttpErrorResponse-like and meets conditions from additional params
 */
export function isInputError(error: unknown, statusCode: number, options?: ErrorOptionsInput): boolean {
  if(
    typeof error === 'function' &&
    error() &&
    'status' in error() &&
    (
      (options?.excludeStatusCode && error()?.status !== statusCode) ||
      (!options?.excludeStatusCode && error()?.status === statusCode)
    )
    &&
    (
      !options?.acceptedErrorCodes ||
      errorContainsCode(error(), options.acceptedErrorCodes)
    )
    &&
    (
      !options?.includedErrorMessage ||
      errorContainsMessage(error(), options.includedErrorMessage)
    )
  ) {
    return true;
  }
  if(
    typeof error === 'object' &&
    error &&
    'status' in error &&
    (
      (options?.excludeStatusCode && error?.status !== statusCode) ||
      (!options?.excludeStatusCode && error?.status === statusCode)
    )
    &&
    (
      !options?.acceptedErrorCodes ||
      errorContainsCode(error, options.acceptedErrorCodes)
    )
    &&
    (
      !options?.includedErrorMessage ||
      errorContainsMessage(error, options.includedErrorMessage)
    )
  ) {
    return true;
  }

  return false;
}

interface ErrorOptionsInput {
  acceptedErrorCodes?: string[],
  includedErrorMessage?: string,
  excludeStatusCode?: true,
}

function errorContainsCode(error: object, acceptedErrorCodes: string[]): boolean {
  return !!(
    'errorCode' in error &&
    typeof error.errorCode === 'string' &&
    acceptedErrorCodes.includes(error.errorCode) ||
    'error' in error &&
    error.error &&
    typeof error.error === 'object' &&
    'errorCode' in error.error &&
    typeof error.error.errorCode === 'string' &&
    acceptedErrorCodes.includes(error.error.errorCode)
  );
}


function errorContainsMessage(error: object, includedErrorMessage: string): boolean {
  return !!(
    includedErrorMessage &&
    'message' in error &&
    typeof error.message === 'string' &&
    error.message.includes(includedErrorMessage) ||
    'error' in error &&
    error.error &&
    typeof error.error === 'object' &&
    'message' in error.error &&
    typeof error.error.message === 'string' &&
    error.error.message.includes(includedErrorMessage)
  );
}


// transform from unix to ms
export function parseTimestamps(data: ApiModelPortfolioChartOut): ApiModelPortfolioChartOut {
  for (const key in data) {
    for (const point of data[key]) {
      point.x = point.x * 1000;
    }
  }

  return data;
}

// to transform blog html content gained from Fondshop to plain styles
export function transformClassesToStyles(html: string): string {
  return html.replace(
    /<(\w+)([^>]*?)class="([^"]*?)((?:fs-)?(?:background-color|color)--(?:hsl-\d+-\d+-\d+|[a-fA-F0-9]{6}))([^"]*?)"([^>]*?)>/g,
    (match, tagName, before, classPrefix, colorClass, classSuffix, after) => {
      let styleToAdd = '';

      const hslMatch = colorClass.match(/(?:fs-)?background-color--hsl-(\d+)-(\d+)-(\d+)/);
      if (hslMatch) {
        styleToAdd = `background-color: hsl(${hslMatch[1]}, ${hslMatch[2]}%, ${hslMatch[3]}%);`;
      }

      const hexBgMatch = colorClass.match(/(?:fs-)?background-color--([a-fA-F0-9]{6})/);
      if (hexBgMatch) {
        styleToAdd = `background-color: #${hexBgMatch[1]};`;
      }

      if (!styleToAdd) {
        const hexColorMatch = colorClass.match(/(?:fs-)?color--([a-fA-F0-9]{6})/);
        if (hexColorMatch) {
          styleToAdd = `color: #${hexColorMatch[1]};`;
        }
      }

      const remainingClasses = (classPrefix + classSuffix).trim();
      const classAttr = remainingClasses ? ` class="${remainingClasses}"` : '';

      const combinedAttrs = before + after;
      const styleMatch = combinedAttrs.match(/style="([^"]*)"/);

      if (styleMatch) {
        const existingStyles = styleMatch[1].trim();
        const separator = existingStyles && !existingStyles.endsWith(';') ? '; ' : ' ';
        const updatedAttrs = combinedAttrs.replace(
          /style="([^"]*)"/,
          `style="${existingStyles}${separator}${styleToAdd}"`
        );
        return `<${tagName}${updatedAttrs}${classAttr}>`;
      } else {
        return `<${tagName}${before}${after}${classAttr} style="${styleToAdd}">`;
      }
    }
  ).replaceAll('style=""', '');
};
